import useApi from "../../services/auth/useApi";
import { BlockType, UpdateBlockType, UpdateRecurrenceBlockType } from "../../types/block/type";
import blockAPI from "../../api/BlockAPI";
import { useQueryClient } from "@tanstack/react-query";
import { inboxQueryKeys } from "./queries";
import { convertClientToServerBlockType } from "../../utils/common/block/type-converter";
import { debounceInvalidate } from "../../services/optimistic-updates/debounceInvalidate";
import { isAxiosError } from "axios";

export const useUpdateInboxBasicBlockMutationOptions = () => {
  const api = useApi();
  return {
    mutationFn: ({ id, payload }: { id: number; payload: UpdateBlockType }) => {
      const serverPayload = convertClientToServerBlockType({
        clientBlock: payload,
        isUpdate: true,
      });
      return blockAPI.updateBlockById(api, id, serverPayload);
    },
    onSuccess: () => {
      debounceInvalidate.enqueueInvalidate(inboxQueryKeys.all());
    },
  };
};

export const useUpdateRecurrenceInboxBlockMutationOptions = () => {
  const api = useApi();
  return {
    mutationFn: ({ id, payload }: { id: number; payload: UpdateRecurrenceBlockType }) => {
      const serverPayload = convertClientToServerBlockType({
        clientBlock: payload,
        isUpdate: true,
      });
      return blockAPI.updateRecurrenceBlockById(api, id, serverPayload);
    },
  };
};

export const useDeleteInboxBlockMutationOptions = () => {
  const api = useApi();
  return {
    mutationFn: (id: number) => blockAPI.deleteBlockById(api, id),
  };
};

export const useReorderInboxBlockMutationOptions = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const inboxGeneralQueryKey = inboxQueryKeys.inboxGeneral();

  return {
    mutationFn: (payload: { ids: number[] }) => blockAPI.updateBlocksOrder(api, payload),
    onMutate: async (payload: { ids: number[] }) => {
      await queryClient.cancelQueries({ queryKey: inboxGeneralQueryKey });
      const previousData = queryClient.getQueryData<BlockType[]>(inboxGeneralQueryKey);
      if (previousData) {
        queryClient.setQueryData<BlockType[]>(inboxGeneralQueryKey, (oldData) => {
          if (!oldData) return [];

          const blockMap = new Map(oldData.map((block) => [block.id, block]));

          const reorderedBlocks = payload.ids
            .map((id) => blockMap.get(id))
            .filter((block): block is BlockType => block !== undefined);

          const remainingBlocks = oldData.filter((block) => !payload.ids.includes(block.id));

          return [...reorderedBlocks, ...remainingBlocks];
        });
      }
    },
    onError: (err: Error) => {
      if (isAxiosError(err)) {
        // inboxGeneral은 dnd때문에 전역상태를 사용하고 있어서 에러 시 invalidate함
        debounceInvalidate.enqueueInvalidate(inboxGeneralQueryKey, {
          shouldInvalidateImmediately: true,
        });
      }
    },
  };
};
